<template>
  <div class="card higashikawa-stockholder-visitor-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ $t('dashboard.stats.title_higashikawa_stockholder_visitors') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.higashikawaStockholderVisitorCard')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h3>
    </div>
    <div class="card-body">
      <!-- Higashikawa Stockholder Visitor Rate - Main Value -->
      <div class="main-value" @click="showDetailModal">
        <span class="number">{{ higashikawaStockholderRate.toFixed(1) }}</span>
        <span class="unit">%</span>
      </div>

      <!-- Trend Indicator -->
      <div class="trend" v-if="trend">
        <span class="trend-label" :class="trendClass" @click="showDetailModal">
          <i :class="trendIcon"></i>
          {{ trendLabel }}
        </span>
      </div>
    </div>

    <!-- Detail Modal -->
    <div class="modal fade" id="higashikawaStockholderVisitorDetailModal" tabindex="-1" aria-labelledby="higashikawaStockholderVisitorDetailModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="higashikawaStockholderVisitorDetailModalLabel">{{ $t('dashboard.stats.title_higashikawa_stockholder_visitors') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.higashikawaStockholderVisitorDetail.selectedPeriod') }}：</span>
              <span class="detail-value">
                {{ higashikawaStockholderRate.toFixed(1) }}% ({{ $t('dashboard.higashikawaStockholderVisitorDetail.higashikawaStockholderVisitors') }} {{ higashikawaStockholderVisitorsCount.toLocaleString() }}{{ $t('dashboard.higashikawaStockholderVisitorDetail.personUnit') }} / {{ $t('dashboard.higashikawaStockholderVisitorDetail.total') }} {{ totalVisitorsCount.toLocaleString() }}{{ $t('dashboard.higashikawaStockholderVisitorDetail.personUnit') }})
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.higashikawaStockholderVisitorDetail.previousYearPeriod') }}：</span>
              <span class="detail-value">
                {{ previousYearHigashikawaStockholderRate.toFixed(1) }}% ({{ $t('dashboard.higashikawaStockholderVisitorDetail.higashikawaStockholderVisitors') }} {{ previousYearHigashikawaStockholderVisitorsCount.toLocaleString() }}{{ $t('dashboard.higashikawaStockholderVisitorDetail.personUnit') }} / {{ $t('dashboard.higashikawaStockholderVisitorDetail.total') }} {{ previousYearTotalVisitorsCount.toLocaleString() }}{{ $t('dashboard.higashikawaStockholderVisitorDetail.personUnit') }})
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.higashikawaStockholderVisitorDetail.changeRate') }}：</span>
              <span class="detail-value" :class="trendClass">
                <i :class="trendIcon"></i>
                {{ trend || 'N/A' }}
              </span>
            </div>
            <div class="detail-row">
              <span class="additional-note">{{ $t('dashboard.higashikawaStockholderVisitorDetail.additionalNote') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';

const { t: $t } = useI18n();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true
  }
});

// Data
const higashikawaStockholderRate = ref(0);
const trend = ref(null);
const trendDirection = ref(null);
const higashikawaStockholderVisitorsCount = ref(0);
const totalVisitorsCount = ref(0);
const previousYearHigashikawaStockholderRate = ref(0);
const previousYearHigashikawaStockholderVisitorsCount = ref(0);
const previousYearTotalVisitorsCount = ref(0);
const loading = ref(false);

// Computed trend class
const trendClass = computed(() => {
  if (!trendDirection.value) return '';
  switch (trendDirection.value) {
    case 'up': return 'trend-up';
    case 'down': return 'trend-down';
    case 'same': return 'trend-same';
    case 'na': return 'trend-na';
    default: return '';
  }
});

// Computed trend icon
// Increased: arrow-up
// Decreased: arrow-down
// Same: arrow-right
// N/A: dash-circle
const trendIcon = computed(() => {
  if (!trendDirection.value) return '';
  switch (trendDirection.value) {
    case 'up': return 'bi bi-arrow-up';
    case 'down': return 'bi bi-arrow-down';
    case 'same': return 'bi bi-arrow-right';
    case 'na': return 'bi bi-dash-circle';
    default: return '';
  }
});

// Computed trend label (localized)
const trendLabel = computed(() => {
  if (!trend.value) return '';
  if (trendDirection.value === 'na') return $t('dashboard.stats.trend_na');
  return trend.value;
});

// Modal instance
let modalInstance = null;

// Show detail modal
const showDetailModal = () => {
  if (!modalInstance) {
    const modalElement = document.getElementById('higashikawaStockholderVisitorDetailModal');
    if (modalElement) {
      modalInstance = new Modal(modalElement);
    }
  }
  modalInstance?.show();
};

// Fetch statistics data
const fetchStatistics = async () => {
  if (!props.dateRange || !props.dateRange.startDate || !props.dateRange.endDate) {
    return;
  }

  loading.value = true;
  try {
    const startDate = formatDate(props.dateRange.startDate);
    const endDate = formatDate(props.dateRange.endDate);

    const response = await axios.get('/api/user/statistics/dashboard/higashikawa-stockholder-visitors', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });

    if (response.data) {
      higashikawaStockholderRate.value = response.data.higashikawa_stockholder_rate ?? 0;
      trend.value = response.data.trend ?? null;
      trendDirection.value = response.data.trend_direction ?? null;
      higashikawaStockholderVisitorsCount.value = response.data.higashikawa_stockholder_visitors_count ?? 0;
      totalVisitorsCount.value = response.data.total_visitors_count ?? 0;
      previousYearHigashikawaStockholderRate.value = response.data.previous_year?.higashikawa_stockholder_rate ?? 0;
      previousYearHigashikawaStockholderVisitorsCount.value = response.data.previous_year?.higashikawa_stockholder_visitors_count ?? 0;
      previousYearTotalVisitorsCount.value = response.data.previous_year?.total_visitors_count ?? 0;
    }
  } catch (error) {
    console.error('Error fetching higashikawa stockholder visitor statistics:', error);
    // Set default values on error
    higashikawaStockholderRate.value = 0;
    trend.value = null;
    trendDirection.value = null;
    higashikawaStockholderVisitorsCount.value = 0;
    totalVisitorsCount.value = 0;
    previousYearHigashikawaStockholderRate.value = 0;
    previousYearHigashikawaStockholderVisitorsCount.value = 0;
    previousYearTotalVisitorsCount.value = 0;
  } finally {
    loading.value = false;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

onMounted(() => {
  fetchStatistics();
});

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchStatistics();
}, { deep: true });
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.card-header {
  background-color: v-bind('colors.white');
  border: none;
  padding: 0;
  margin-bottom: 16px;
}

.card-body {
  padding: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0;

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }
}

.higashikawa-stockholder-visitor-card {
  .main-value {
    font-size: 48px;
    font-weight: 700;
    color: v-bind('colors.textPrimary');
    margin-bottom: 8px;
    cursor: pointer;

    .unit {
      font-size: 24px;
      font-weight: 400;
      color: v-bind('colors.gray[500]');
      margin-left: 4px;
    }
  }

  .trend {
    .trend-label {
      font-size: 14px;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 12px;
      border-radius: 16px;
      cursor: pointer;

      i {
        font-size: 12px;
      }

      // Increase
      &.trend-up {
        background-color: v-bind('colors.trendUp');
        color: v-bind('colors.white');
      }

      // Decrease
      &.trend-down {
        background-color: v-bind('colors.trendDown');
        color: v-bind('colors.white');
      }

      // Same
      &.trend-same {
        background-color: v-bind('colors.trendSame');
        color: v-bind('colors.white');
      }

      // N/A (No previous year data)
      &.trend-na {
        background-color: v-bind('colors.gray[400]');
        color: v-bind('colors.white');
      }
    }
  }
}

// Modal styles
.modal-content {
  .modal-header {
    background-color: v-bind('colors.white');
    border-bottom: 1px solid v-bind('colors.gray[200]');

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .modal-body {
    padding: 24px;

    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 12px 0;
      border-bottom: 1px solid v-bind('colors.gray[100]');
      gap: 16px;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
        flex-shrink: 0;
      }

      .additional-note {
        font-size: 12px;
        font-weight: 400;
        white-space: normal;
        color: v-bind('colors.textLight');
        word-wrap: break-word;
        overflow-wrap: break-word;
        flex-shrink: 1;
      }

      .detail-value {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        display: inline-flex;
        align-items: center;
        gap: 4px;
        text-align: right;
        flex: 1;

        &.trend-up {
          color: v-bind('colors.trendUp');
        }

        &.trend-down {
          color: v-bind('colors.trendDown');
        }

        &.trend-same {
          color: v-bind('colors.trendSame');
        }

        &.trend-na {
          color: v-bind('colors.trendSame');
        }

        i {
          font-size: 14px;
        }
      }
    }
  }
}
</style>